import React, { useEffect } from 'react';

const PrivacyPolicy = ({ navigateTo }) => {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="doc-page">
      <div className="doc-hero">
        <div className="doc-breadcrumb">
          <button onClick={() => navigateTo('home')}>SequPay</button>
          <span>›</span><span>Privacy Policy</span>
        </div>
        <h1>Privacy Policy</h1>
        <p>Effective Date: 1 May 2025 &nbsp;·&nbsp; Version 1.0 &nbsp;·&nbsp; SequSpace Technologies Pvt. Ltd.</p>
      </div>
      <div className="doc-body">
        <div className="doc-alert">
          <strong>Important:</strong> SequPay is currently an <strong>early-access preview application</strong>. No live financial transactions are processed through the app at this stage. All payment, loan, investment, and insurance screens are UI demonstrations pending regulatory approvals and banking integrations. This policy accurately describes exactly what data the app collects today.
        </div>

        <div className="doc-toc">
          <h5>Table of Contents</h5>
          <div className="doc-toc-grid">
            <button onClick={() => scrollTo('pp1')}>1. Who We Are</button>
            <button onClick={() => scrollTo('pp6')}>6. Data Retention</button>
            <button onClick={() => scrollTo('pp2')}>2. Data We Collect</button>
            <button onClick={() => scrollTo('pp7')}>7. Data Security</button>
            <button onClick={() => scrollTo('pp3')}>3. Permissions</button>
            <button onClick={() => scrollTo('pp8')}>8. Your Rights</button>
            <button onClick={() => scrollTo('pp4')}>4. How We Use Data</button>
            <button onClick={() => scrollTo('pp9')}>9. Children's Privacy</button>
            <button onClick={() => scrollTo('pp5')}>5. Data Sharing</button>
            <button onClick={() => scrollTo('pp10')}>10. Contact Us</button>
          </div>
        </div>

        <div className="doc-sec"  id="pp1">
          <h2><span className="doc-sec-num">1</span>Who We Are</h2>
          <p>SequPay is a digital payment and financial services application operated by <strong>SequSpace Technologies Pvt. Ltd.</strong> ("Company", "we", "us", or "our").</p>
          <p>At this stage, SequPay is an <strong>early-access application</strong>. No live financial transactions are currently processed through the app. Features will be activated progressively as banking partner integrations are completed and all required regulatory approvals are obtained.</p>
          <p>By using SequPay, you agree to this Privacy Policy.</p>
          <p><strong>Company:</strong> SequSpace Technologies Pvt. Ltd.<br/>
          <strong>Privacy Email:</strong> <a href="mailto:privacy@sequspace.com">privacy@sequspace.com</a><br/>
          <strong>Support Email:</strong> <a href="mailto:support@sequspace.com">support@sequspace.com</a><br/>
          <strong>Website:</strong> <a href="https://sequspace.com" target="_blank" rel="noopener noreferrer">https://sequspace.com</a><br/>
          <strong>Grievance Officer:</strong> Contact via <a href="mailto:privacy@sequspace.com">privacy@sequspace.com</a></p>
        </div>

        <div className="doc-sec" id="pp2">
          <h2><span className="doc-sec-num">2</span>What Data We Collect</h2>
          <p><strong>2.1 — Data You Provide Directly</strong></p>
          <ul>
            <li>Full name, mobile number, and email address when you create an account</li>
            <li>Date of birth and address for profile setup</li>
            <li>Profile photograph (optional)</li>
            <li>Government ID information submitted during KYC onboarding steps — stored securely; <strong>not transmitted to any bank, NBFC, or financial institution at this stage</strong></li>
            <li>Communications and messages you send to our support team</li>
          </ul>
          <p><strong>2.2 — Data Collected Automatically</strong></p>
          <ul>
            <li>Device identifiers: Device ID, OS version, app version</li>
            <li>IP address and approximate location (city-level only)</li>
            <li>App usage data: screens visited, features tapped, session duration</li>
            <li>Crash reports and diagnostic data (via Firebase Crashlytics)</li>
            <li>Analytics events for app performance improvement (via Firebase Analytics)</li>
          </ul>
          <div className="doc-warn">
            <strong>⚠ Note on financial screen data:</strong> Data entered into loan, payment, wallet, investment, or insurance screens is used <strong>only to demonstrate app functionality</strong> and is <strong>not transmitted to any bank, NBFC, payment network, or financial institution</strong>. No real financial processing occurs at this stage.
          </div>
        </div>

        <div className="doc-sec" id="pp3">
          <h2><span className="doc-sec-num">3</span>Permissions Requested</h2>
          <table className="doc-table">
            <thead><tr><th>Permission</th><th>Why We Need It</th><th>Required / Optional</th></tr></thead>
            <tbody>
              <tr><td><strong>Camera</strong></td><td>QR code scanning and document photography for KYC (demo stage)</td><td>Optional</td></tr>
              <tr><td><strong>Contacts</strong></td><td>Display saved payees and contacts for payment flows (demo)</td><td>Optional</td></tr>
              <tr><td><strong>Location</strong></td><td>Show nearby merchant offers and fraud detection signals (demo)</td><td>Optional</td></tr>
              <tr><td><strong>Storage / Files</strong></td><td>Save transaction receipts and app data locally on device</td><td>Optional</td></tr>
              <tr><td><strong>Phone State / SMS</strong></td><td>Auto-read OTP SMS for verification flows</td><td>Optional</td></tr>
            </tbody>
          </table>
          <p>All permissions are <strong>optional</strong>. You may deny any permission in your device settings at any time.</p>
        </div>

        <div className="doc-sec" id="pp4">
          <h2><span className="doc-sec-num">4</span>How We Use Your Data</h2>
          <ul>
            <li>To create and manage your SequPay account and provide our Services</li>
            <li>To personalise your in-app experience and dashboard</li>
            <li>To send OTPs and account security notifications</li>
            <li>To respond to your support enquiries and messages</li>
            <li>To improve app performance and user experience through crash analytics</li>
            <li>To send product updates and feature launch notifications (you may opt out at any time)</li>
            <li>To comply with applicable legal and regulatory obligations</li>
            <li>To detect and prevent fraud or misuse of the platform</li>
          </ul>
        </div>

        <div className="doc-sec" id="pp5">
          <h2><span className="doc-sec-num">5</span>Data Sharing</h2>
          <p><strong>We may share your data with:</strong></p>
          <ul>
            <li><strong>Cloud infrastructure providers</strong> — for hosting and secure storage</li>
            <li><strong>Analytics providers</strong> — Firebase Analytics and Crashlytics for app performance monitoring</li>
            <li><strong>Legal and regulatory authorities</strong> — when required by applicable law</li>
          </ul>
          <p><strong>We do NOT:</strong></p>
          <ul>
            <li>Sell your personal data to any third party</li>
            <li>Share your data with advertisers, marketing firms, or data brokers</li>
            <li>Transmit your financial or identity data to banks, NBFCs, or payment networks</li>
            <li>Use your data to train third-party AI or ML models</li>
          </ul>
        </div>

        <div className="doc-sec" id="pp6">
          <h2><span className="doc-sec-num">6</span>Data Retention</h2>
          <ul>
            <li><strong>Account data</strong> — retained while your account is active</li>
            <li><strong>Account deletion</strong> — personal data deleted within <strong>30 days</strong> of a deletion request</li>
            <li><strong>Support communications</strong> — retained for up to 2 years</li>
            <li><strong>Crash and diagnostic data</strong> — retained for 90 days</li>
            <li><strong>Analytics data</strong> — retained in aggregated, anonymised form for up to 24 months</li>
          </ul>
        </div>

        <div className="doc-sec" id="pp7">
          <h2><span className="doc-sec-num">7</span>Data Security</h2>
          <ul>
            <li>HTTPS / <strong>TLS 1.3</strong> encryption for all data transmitted to our servers</li>
            <li><strong>256-bit AES encryption</strong> for all data stored at rest</li>
            <li>Passwords hashed using industry-standard bcrypt algorithm</li>
            <li>PIN and biometric authentication on device</li>
            <li>Role-based access controls limiting staff access to personal data</li>
            <li>Regular security reviews and penetration testing</li>
          </ul>
        </div>

        <div className="doc-sec" id="pp8">
          <h2><span className="doc-sec-num">8</span>Your Rights</h2>
          <ul>
            <li><strong>Access</strong> — Request a copy of the personal data we hold about you</li>
            <li><strong>Correction</strong> — Ask us to correct any inaccurate or incomplete data</li>
            <li><strong>Deletion</strong> — Request deletion of your account and associated personal data</li>
            <li><strong>Portability</strong> — Request your data in a machine-readable format</li>
            <li><strong>Consent withdrawal</strong> — Withdraw consent for any optional permission at any time</li>
            <li><strong>Opt-out of marketing</strong> — Unsubscribe from non-essential communications at any time</li>
          </ul>
          <p>To exercise any of these rights, email us at <a href="mailto:privacy@sequspace.com">privacy@sequspace.com</a>. We respond within <strong>30 days</strong>.</p>
          <p>To delete your account directly, use the <button onClick={() => navigateTo('delete')} style={{cursor:'pointer',color:'var(--trustBlue)',background:'none',border:'none',padding:0,fontSize:'inherit',fontFamily:'inherit'}}>Delete Account</button> page.</p>
        </div>

        <div className="doc-sec" id="pp9">
          <h2><span className="doc-sec-num">9</span>Children's Privacy</h2>
          <p>SequPay is <strong>not intended for users under 18 years of age</strong>. We do not knowingly collect data from minors. If you believe a minor has registered on SequPay, please contact us immediately at <a href="mailto:privacy@sequspace.com">privacy@sequspace.com</a>.</p>
        </div>

        <div className="doc-sec">
          <h2><span className="doc-sec-num">—</span>Changes to This Policy</h2>
          <p>We may update this Privacy Policy from time to time. We will notify you of material changes via in-app notification or email at least 14 days before the change takes effect.</p>
        </div>

        <div className="doc-sec" id="pp10">
          <h2><span className="doc-sec-num">10</span>Contact &amp; Grievance Officer</h2>
          <p>If you have any questions about this Privacy Policy, please contact us:</p>
          <div className="doc-contact">
            <h3>SequSpace Technologies Pvt. Ltd.</h3>
            <p>
              <strong style={{color:'#fff'}}>Privacy &amp; Data enquiries:</strong> <a href="mailto:privacy@sequspace.com">privacy@sequspace.com</a><br/>
              <strong style={{color:'#fff'}}>General support:</strong> <a href="mailto:support@sequspace.com">support@sequspace.com</a><br/>
              <strong style={{color:'#fff'}}>Website:</strong> <a href="https://sequspace.com" target="_blank" rel="noopener noreferrer">https://sequspace.com</a><br/>
              <strong style={{color:'#fff'}}>Response time:</strong> Within 30 days of receiving your request
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PrivacyPolicy;
